import {
  CellContentsPayload,
  CellId,
  CellLocation,
  CellType,
  ExploreCellInput,
  FRACTIONAL_INDEX_END,
  FRACTIONAL_INDEX_START,
  GridElementType,
  NoCodeCellDataframe,
  NoCodeCellType,
  createChartCellPayload,
  createDisplayTableCellPayload,
  createExploreCellPayload,
  createFilterCellPayload,
  createPivotCellPayload,
  fractionalIndexMidpoint95,
  newSuggestibleChart,
} from "@hex/common";
import { useCallback } from "react";

import { useGridRowsGetter } from "../../../hex-version-multiplayer/state-hooks/gridRowsStateHooks.js";
import { useInsertCellOp } from "../../../hooks/cell/insert-move/index.js";
import {
  useGetNewDisplayTableCellFields,
  useGetNewFilterCellFields,
  useGetNewPivotCellFields,
} from "../../../hooks/cell/useGetNewCellFields.js";
import { scrollToAppElement } from "../../../hooks/cell/useScrollToCell.js";
import { useSelectCell } from "../../../hooks/cell/useSelectCell.js";
import { useProjectViews } from "../../../hooks/useProjectViews.js";
import { useDispatch } from "../../../redux/hooks.js";
import { setActiveGridElementIds } from "../../../redux/slices/logicViewSlice.js";
import { isInlineCellByType } from "../../../util/cellLayoutHelpers.js";
import { useHexVersionAOContext } from "../../../util/hexVersionAOContext.js";
import { useSelectedTabId } from "../../hex/grid-layout/GridTabHooks.js";
import { useAddEntityToGridLayout } from "../../hex/grid-layout/hooks/useAddToGridLayout.js";

type CreateCellReturnType = {
  createDisplayTableCell: (dataframe: NoCodeCellDataframe) => void;
  createChartCell: (dataframe: NoCodeCellDataframe) => void;
  createPivotCell: (
    dataframe: NoCodeCellDataframe,
    createAsExplore: boolean,
  ) => void;
  createFilterCell: (
    dataframe: NoCodeCellDataframe,
    useQueryMode?: boolean,
  ) => void;
  createExploreCell: (dataframe: ExploreCellInput) => void;
  /**
   * Am also returning the `insertCell` callback used to insert the no code
   * cells. This is useful if you want to insert other cell types with the same
   * behavior.
   */
  insertCell: (cellContents: CellContentsPayload) => void;
};

/**
 * This hook returns callbacks for creating new no code cells with a dataframe
 * input. There are several shared hooks for creating new cells - considering using this hook if:
 *   - you need to dynamically set the insertionType
 *   - you want to insert no-code cells w/ a dataframe
 *   - you want to add the cell to the app layout if in app view
 *   - you want to select the cell and scroll to it after insertion
 *
 * @param insertLocation: Where to actually insert the cell
 * @param runAfterInsertion: Whether to run the cell after insertion.
 * @returns `insertCell` callback that can be used to insert other cell types.
 * This is helpful if you want to insert other cell types with a custom payload
 * but still have the same insertion behavior.
 */
export const useCreateNoCodeCell = ({
  insertLocation,
  onCreateCell,
  runAfterInsertion,
}: {
  insertLocation: CellLocation | (() => CellLocation);
  runAfterInsertion?: boolean;
  /**
   * This will be invoked when any of the `createXCell` functions are called.
   */
  onCreateCell?: (cellType: NoCodeCellType) => void;
}): CreateCellReturnType => {
  const { inAppView } = useProjectViews();

  const getNewPivotCellFields = useGetNewPivotCellFields();
  const getNewFilterCellFields = useGetNewFilterCellFields();
  const getNewDisplayTableCellFields = useGetNewDisplayTableCellFields();

  const { dispatchAO } = useHexVersionAOContext();
  const dispatch = useDispatch();
  const insertCellOp = useInsertCellOp();

  const { addEntityToGridLayoutOperations } = useAddEntityToGridLayout();

  const selectedTabId = useSelectedTabId();
  const getGridRows = useGridRowsGetter({
    selector: (rows) => rows.filter((r) => r.gridLayoutId === selectedTabId),
  });
  const { selectCells } = useSelectCell();

  const addGridElementOp = useCallback(
    ({ cellType, entityId }: { entityId: CellId; cellType: CellType }) => {
      const gridRows = getGridRows();
      // insert at bottom
      const insertAt = fractionalIndexMidpoint95(
        gridRows[gridRows.length - 1]?.order ?? FRACTIONAL_INDEX_START,
        FRACTIONAL_INDEX_END,
      );
      const isInline = isInlineCellByType(cellType);

      return addEntityToGridLayoutOperations({
        type: GridElementType.CELL,
        entityId: entityId,
        isInline,
        tabId: selectedTabId,
        insertAt,
      });
    },
    [addEntityToGridLayoutOperations, getGridRows, selectedTabId],
  );

  const insertCell = useCallback(
    (cellContents: CellContentsPayload) => {
      const createCellOp = insertCellOp(cellContents, {
        runAfterInsertion,
        location:
          typeof insertLocation === "function"
            ? insertLocation()
            : insertLocation,
      });

      const createCellOperation = createCellOp;
      if (inAppView) {
        const { gridElementId, operations: addGridElementOperations } =
          addGridElementOp({
            cellType: cellContents.type,
            entityId: createCellOperation.payload.cellId,
          });
        dispatchAO([createCellOperation, ...addGridElementOperations]);
        if (gridElementId) {
          dispatch(setActiveGridElementIds([gridElementId]));
        }

        setTimeout(() => {
          scrollToAppElement({
            entityId: createCellOperation.payload.cellId,
            options: { scrollBehavior: "smooth" },
          });
        }, 0);
      } else {
        dispatchAO(createCellOperation);
        selectCells(createCellOperation.payload.cellId);
      }
    },
    [
      addGridElementOp,
      dispatch,
      dispatchAO,
      inAppView,
      insertCellOp,
      insertLocation,
      runAfterInsertion,
      selectCells,
    ],
  );

  const createDisplayTableCell = useCallback(
    (dataframe: NoCodeCellDataframe) => {
      insertCell(
        createDisplayTableCellPayload({
          ...getNewDisplayTableCellFields(),
          dataframe,
        }),
      );
      onCreateCell?.(CellType.DISPLAY_TABLE);
    },
    [getNewDisplayTableCellFields, insertCell, onCreateCell],
  );
  const createChartCell = useCallback(
    (dataframe: NoCodeCellDataframe) => {
      const chartSpec = newSuggestibleChart(dataframe);
      const chartCellPayload = createChartCellPayload({
        chartSpec,
      });

      insertCell(chartCellPayload);
      onCreateCell?.(CellType.CHART);
    },
    [insertCell, onCreateCell],
  );
  const createPivotCell = useCallback(
    (dataframe: NoCodeCellDataframe, createAsExplore: boolean) => {
      insertCell(
        createPivotCellPayload({
          ...getNewPivotCellFields(),
          dataframe,
          createAsExplore,
        }),
      );
      onCreateCell?.(createAsExplore ? CellType.EXPLORE : CellType.PIVOT);
    },
    [getNewPivotCellFields, insertCell, onCreateCell],
  );

  const createFilterCell = useCallback(
    (dataframe: NoCodeCellDataframe, useQueryMode?: boolean) => {
      insertCell(
        createFilterCellPayload({
          ...getNewFilterCellFields(),
          dataframe,
          useQueryMode,
        }),
      );
      onCreateCell?.(CellType.FILTER);
    },
    [getNewFilterCellFields, insertCell, onCreateCell],
  );

  const createExploreCell = useCallback(
    (dataframe: ExploreCellInput) => {
      insertCell(
        createExploreCellPayload({
          input: dataframe,
          viewType: "visualization",
        }),
      );
      onCreateCell?.(CellType.EXPLORE);
    },
    [insertCell, onCreateCell],
  );

  return {
    createChartCell,
    createDisplayTableCell,
    createFilterCell,
    createPivotCell,
    createExploreCell,
    insertCell,
  };
};
